import React from 'react';
import {
  Facebook,
  CheckCircle,
  ArrowRight,
  AlertCircle,
  Shield,
  Zap,
  TrendingUp,
  Link as LinkIcon,
  Settings
} from 'lucide-react';
import ModernDocHeader from '../../../components/documentation/ModernDocHeader';

const FacebookIntegrationDocs: React.FC = () => {
  return (
    <div>
      <ModernDocHeader
        title="Facebook Pages Integration"
        description="Connect your Facebook Pages to automatically capture leads from Facebook Lead Ads and Page messages. Seamless integration with Facebook's Business Platform."
        icon={<Facebook className="w-6 h-6" />}
        badge="Essential"
        badgeColor="purple"
      />

      <div className="p-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 max-w-4xl">
          <div className="prose prose-blue dark:prose-invert max-w-none">

      {/* Overview */}
      <div className="not-prose bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-2">What You'll Learn</h3>
            <ul className="space-y-1 text-sm text-blue-800 dark:text-blue-300">
              <li>✓ How to connect your Facebook Pages</li>
              <li>✓ Automatically capture Facebook Lead Ads</li>
              <li>✓ Sync lead ad forms and fields</li>
              <li>✓ Handle Page messages and inquiries</li>
              <li>✓ Manage multiple Facebook Pages</li>
              <li>✓ OAuth authentication process</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>Why Integrate Facebook Pages?</h2>
      <p>
        Facebook is one of the most powerful lead generation platforms. By integrating your Facebook Pages
        with your CRM, you can:
      </p>

      <div className="not-prose grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
        <div className="p-4 border-2 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <div className="flex items-center space-x-3 mb-2">
            <Zap className="w-5 h-5 text-blue-600" />
            <h4 className="font-semibold text-blue-900 dark:text-blue-200">Instant Lead Capture</h4>
          </div>
          <p className="text-sm text-blue-700 dark:text-blue-300">
            Automatically sync leads from Facebook Lead Ads within seconds
          </p>
        </div>

        <div className="p-4 border-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 rounded-lg">
          <div className="flex items-center space-x-3 mb-2">
            <TrendingUp className="w-5 h-5 text-green-600" />
            <h4 className="font-semibold text-green-900 dark:text-green-200">Track Campaign ROI</h4>
          </div>
          <p className="text-sm text-green-700 dark:text-green-300">
            See which Facebook campaigns generate the best quality leads
          </p>
        </div>

        <div className="p-4 border-2 border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
          <div className="flex items-center space-x-3 mb-2">
            <Shield className="w-5 h-5 text-purple-600" />
            <h4 className="font-semibold text-purple-900 dark:text-purple-200">Secure OAuth</h4>
          </div>
          <p className="text-sm text-purple-700 dark:text-purple-300">
            Safe, official Facebook OAuth integration - no manual API keys
          </p>
        </div>

        <div className="p-4 border-2 border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
          <div className="flex items-center space-x-3 mb-2">
            <LinkIcon className="w-5 h-5 text-orange-600" />
            <h4 className="font-semibold text-orange-900 dark:text-orange-200">Multi-Page Support</h4>
          </div>
          <p className="text-sm text-orange-700 dark:text-orange-300">
            Connect multiple Facebook Pages from a single account
          </p>
        </div>
      </div>

      <h2>What Gets Captured</h2>
      <div className="not-prose bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 my-6">
        <div className="flex items-start space-x-3">
          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-base font-semibold text-green-900 dark:text-green-200 mb-3">
              Automatic Lead Data Sync
            </h3>
            <ul className="space-y-2 text-sm text-green-800 dark:text-green-300">
              <li>✓ <strong>Full Name</strong> - Customer's name from form submission</li>
              <li>✓ <strong>Email Address</strong> - Contact email</li>
              <li>✓ <strong>Phone Number</strong> - If included in lead form</li>
              <li>✓ <strong>Custom Form Fields</strong> - Any additional questions you've added</li>
              <li>✓ <strong>Ad Campaign Details</strong> - Which ad generated the lead</li>
              <li>✓ <strong>Form Name</strong> - Lead form identifier</li>
              <li>✓ <strong>Submission Time</strong> - Exact timestamp</li>
              <li>✓ <strong>Facebook Page Info</strong> - Source page details</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>Prerequisites</h2>
      <p>Before you begin, make sure you have:</p>
      <ul>
        <li><strong>Facebook Business Account</strong> - Required for managing Pages</li>
        <li><strong>Facebook Page Admin Access</strong> - You must be an admin of the Page</li>
        <li><strong>Active Facebook Lead Ads</strong> - Running lead generation campaigns</li>
        <li><strong>Lead Forms Created</strong> - At least one lead form configured</li>
      </ul>

      <div className="not-prose bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 my-4">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          <strong>💡 Don't have a Facebook Business Account?</strong>
        </p>
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Visit{' '}
          <a
            href="https://business.facebook.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            business.facebook.com
          </a>
          {' '}to create one for free.
        </p>
      </div>

      <h2>Setup Guide</h2>

      <h3>Step 1: Navigate to Facebook Pages Settings</h3>
      <ol>
        <li>In your CRM, go to <code>Settings → Facebook Pages</code></li>
        <li>You'll see the Facebook Pages integration dashboard</li>
        <li>Click <strong>"Connect Facebook Page"</strong> button</li>
      </ol>

      <h3>Step 2: Authenticate with Facebook (OAuth)</h3>
      <ol>
        <li>A Facebook login popup will appear</li>
        <li>Log in with your Facebook account credentials</li>
        <li>Review the permissions requested:
          <ul>
            <li><strong>pages_show_list</strong> - See your Pages</li>
            <li><strong>pages_manage_metadata</strong> - Access Page information</li>
            <li><strong>leads_retrieval</strong> - Read lead forms and submissions</li>
          </ul>
        </li>
        <li>Click <strong>"Continue"</strong> to grant permissions</li>
      </ol>

      <div className="not-prose bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 my-4">
        <div className="flex items-start space-x-2">
          <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-blue-800 dark:text-blue-300 mb-2">
              <strong>Security Note:</strong>
            </p>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              We use Facebook's official OAuth 2.0 flow. Your Facebook password is never shared with our CRM.
              You can revoke access anytime from Facebook's Settings.
            </p>
          </div>
        </div>
      </div>

      <h3>Step 3: Select Facebook Pages</h3>
      <ol>
        <li>After authentication, you'll see a list of your Facebook Pages</li>
        <li>Select the Pages you want to connect</li>
        <li>You can choose one or multiple Pages</li>
        <li>Click <strong>"Connect Selected Pages"</strong></li>
      </ol>

      <h3>Step 4: Configure Lead Forms</h3>
      <ol>
        <li>For each connected Page, you'll see associated Lead Forms</li>
        <li>Enable the forms you want to sync:
          <ul>
            <li>Toggle <strong>ON</strong> for active lead forms</li>
            <li>Toggle <strong>OFF</strong> for forms you don't want to capture</li>
          </ul>
        </li>
        <li>Map form fields to CRM fields (automatic for common fields)</li>
        <li>Click <strong>"Save Configuration"</strong></li>
      </ol>

      <h3>Step 5: Test the Integration</h3>
      <ol>
        <li>Submit a test lead through your Facebook Lead Ad form</li>
        <li>Check your CRM dashboard within 30 seconds</li>
        <li>Verify the lead appears with:
          <ul>
            <li>Correct name and email</li>
            <li>Custom field data</li>
            <li>Source marked as "Facebook"</li>
            <li>Page name in lead details</li>
          </ul>
        </li>
      </ol>

      <div className="not-prose bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 my-6">
        <div className="flex items-start space-x-3">
          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-green-800 dark:text-green-300 mb-2">
              <strong>Testing Tip:</strong>
            </p>
            <p className="text-sm text-green-700 dark:text-green-300">
              Use Facebook's "Test Form" feature in Ads Manager to submit test leads without running actual ads.
            </p>
          </div>
        </div>
      </div>

      <h2>How It Works</h2>

      <h3>Lead Sync Process</h3>
      <ol>
        <li><strong>User Submits Form</strong> - Customer fills out Facebook Lead Ad form</li>
        <li><strong>Facebook Processes</strong> - Form submission saved to Facebook</li>
        <li><strong>Webhook Triggered</strong> - Facebook sends real-time notification to CRM</li>
        <li><strong>CRM Retrieves Data</strong> - System fetches full lead details via Facebook API</li>
        <li><strong>Lead Created</strong>:
          <ul>
            <li>Checks for duplicate (email/phone)</li>
            <li>Maps form fields to CRM fields</li>
            <li>Creates lead with source = "Facebook"</li>
            <li>Assigns to team member (if Round Robin enabled)</li>
            <li>Sends notification (if enabled)</li>
          </ul>
        </li>
        <li><strong>Lead Available</strong> - Appears in dashboard instantly</li>
      </ol>

      <h3>Field Mapping</h3>
      <p>Facebook form fields automatically map to CRM fields:</p>

      <div className="not-prose">
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden my-6">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">Facebook Field</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">CRM Field</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">Auto-Mapped</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              <tr>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">full_name</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300"><code>name</code></td>
                <td className="px-4 py-3 text-green-600 dark:text-green-400">✓ Yes</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">email</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300"><code>email</code></td>
                <td className="px-4 py-3 text-green-600 dark:text-green-400">✓ Yes</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">phone_number</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300"><code>phone</code></td>
                <td className="px-4 py-3 text-green-600 dark:text-green-400">✓ Yes</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">company_name</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300"><code>company</code></td>
                <td className="px-4 py-3 text-green-600 dark:text-green-400">✓ Yes</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Custom questions</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300"><code>custom_fields</code></td>
                <td className="px-4 py-3 text-orange-600 dark:text-orange-400">Manual</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <h2>Managing Multiple Pages</h2>
      <p>You can connect and manage multiple Facebook Pages:</p>

      <h3>Adding More Pages</h3>
      <ol>
        <li>Go to <code>Settings → Facebook Pages</code></li>
        <li>Click <strong>"Add Another Page"</strong></li>
        <li>Follow the OAuth flow again</li>
        <li>Select additional Pages to connect</li>
      </ol>

      <h3>Disconnecting a Page</h3>
      <ol>
        <li>Find the Page in your connected pages list</li>
        <li>Click the <strong>"Disconnect"</strong> button</li>
        <li>Confirm disconnection</li>
        <li>Note: Existing leads from that Page remain in CRM</li>
      </ol>

      <h3>Viewing Page Statistics</h3>
      <p>For each connected Page, you can see:</p>
      <ul>
        <li>Total leads captured</li>
        <li>Active lead forms</li>
        <li>Last sync time</li>
        <li>Connection status</li>
      </ul>

      <h2>Custom Field Mapping</h2>
      <p>
        If you use custom questions in your Facebook Lead Forms, you can map them to custom CRM fields:
      </p>

      <h3>Step-by-Step Mapping</h3>
      <ol>
        <li>Navigate to <code>Settings → Facebook Pages</code></li>
        <li>Click <strong>"Configure"</strong> on a connected Page</li>
        <li>Select a Lead Form</li>
        <li>Click <strong>"Field Mapping"</strong></li>
        <li>For each custom question:
          <ul>
            <li>Select the corresponding CRM custom field</li>
            <li>Or create a new custom field on the spot</li>
            <li>Choose data type (Text, Number, Date, etc.)</li>
          </ul>
        </li>
        <li>Click <strong>"Save Mapping"</strong></li>
      </ol>

      <div className="not-prose bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 my-4">
        <p className="text-sm text-blue-800 dark:text-blue-300">
          <strong>💡 Best Practice:</strong> Create custom fields in CRM first (Settings → Form Fields),
          then map Facebook questions to them. This gives you better control over field types and validation.
        </p>
      </div>

      <h2>Troubleshooting</h2>

      <div className="not-prose bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700 my-4">
        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Leads not syncing from Facebook?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400 space-y-2">
            <p><strong>Check these common issues:</strong></p>
            <ul className="list-disc list-inside space-y-1">
              <li>Page is still connected (Settings → Facebook Pages)</li>
              <li>Lead form is enabled/active in configuration</li>
              <li>Webhook subscription is active (CRM auto-manages this)</li>
              <li>Your Facebook Page token hasn't expired (reconnect if needed)</li>
              <li>Lead form has submissions (check Facebook Ads Manager)</li>
            </ul>
          </div>
        </details>

        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            "Permission Denied" error during OAuth?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            <p>This usually means:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>You're not an admin of the Facebook Page</li>
              <li>Your Facebook account doesn't have Business Manager access</li>
              <li>Required permissions were denied during OAuth</li>
            </ul>
            <p className="mt-2"><strong>Solution:</strong> Ask a Page Admin to connect, or request admin access to the Page.</p>
          </div>
        </details>

        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Custom fields not mapping correctly?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            <p>Ensure:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Custom field exists in CRM (Settings → Form Fields)</li>
              <li>Field mapping is saved in Facebook Pages configuration</li>
              <li>Data types match (Text to Text, Number to Number, etc.)</li>
              <li>Facebook question has an answer (not skipped by user)</li>
            </ul>
          </div>
        </details>

        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Page token expired message?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            <p>
              Facebook Page tokens expire after 60 days. To refresh:
            </p>
            <ol className="list-decimal list-inside space-y-1 mt-2">
              <li>Go to Settings → Facebook Pages</li>
              <li>Click <strong>"Reconnect"</strong> on the expired Page</li>
              <li>Complete OAuth flow again</li>
              <li>Token is refreshed for another 60 days</li>
            </ol>
          </div>
        </details>

        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Duplicate leads being created?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            <p>
              CRM detects duplicates by email or phone. If duplicates appear:
            </p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Check if leads have different email/phone in Facebook forms</li>
              <li>Verify your duplicate detection settings</li>
              <li>Users might be submitting forms multiple times</li>
            </ul>
            <p className="mt-2">This is usually expected behavior if user actually submitted multiple times.</p>
          </div>
        </details>
      </div>

      <h2>Facebook Lead Ads Best Practices</h2>

      <div className="not-prose space-y-4 my-6">
        <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4">
          <div className="flex items-start space-x-3">
            <Settings className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-blue-900 dark:text-blue-200 mb-1">
                Keep Forms Short
              </h4>
              <p className="text-sm text-blue-800 dark:text-blue-300">
                Forms with 3-5 fields have 70% higher completion rates than longer forms.
                Only ask for essential information.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 p-4">
          <div className="flex items-start space-x-3">
            <Zap className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-green-900 dark:text-green-200 mb-1">
                Respond Within 5 Minutes
              </h4>
              <p className="text-sm text-green-800 dark:text-green-300">
                Facebook leads expect quick responses. Enable CRM notifications and
                set up auto-assignment for instant follow-up.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-purple-50 dark:bg-purple-900/20 border-l-4 border-purple-500 p-4">
          <div className="flex items-start space-x-3">
            <TrendingUp className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-purple-900 dark:text-purple-200 mb-1">
                Track Campaign Performance
              </h4>
              <p className="text-sm text-purple-800 dark:text-purple-300">
                Use CRM reports to see which Facebook campaigns deliver quality leads.
                Filter leads by source and analyze conversion rates.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-orange-50 dark:bg-orange-900/20 border-l-4 border-orange-500 p-4">
          <div className="flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-orange-900 dark:text-orange-200 mb-1">
                Set Up Thank You Pages
              </h4>
              <p className="text-sm text-orange-800 dark:text-orange-300">
                Configure instant experience or thank you screens in your Lead Ads to
                set expectations for follow-up contact.
              </p>
            </div>
          </div>
        </div>
      </div>

      <h2>Advanced Configuration</h2>

      <h3>Lead Quality Filters</h3>
      <p>You can configure automatic lead quality scoring based on Facebook data:</p>
      <ol>
        <li>Go to <code>Settings → Facebook Pages → Advanced</code></li>
        <li>Enable "Lead Quality Scoring"</li>
        <li>Set criteria:
          <ul>
            <li>Organic vs Paid traffic</li>
            <li>Form completion time (too fast = likely bot)</li>
            <li>Email domain validation</li>
            <li>Phone number format validation</li>
          </ul>
        </li>
        <li>Low quality leads can be auto-tagged for review</li>
      </ol>

      <h3>Auto-Response Integration</h3>
      <p>Configure automated responses for Facebook leads:</p>
      <ol>
        <li>Navigate to <code>Settings → Drip Campaigns</code></li>
        <li>Create a campaign triggered by "Source = Facebook"</li>
        <li>Set instant welcome email</li>
        <li>Schedule follow-up sequence</li>
      </ol>

      <h2>Privacy & Compliance</h2>

      <div className="not-prose bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 my-8">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-lg font-semibold text-yellow-900 dark:text-yellow-200 mb-2">
              GDPR & Data Privacy
            </h4>
            <ul className="space-y-2 text-sm text-yellow-800 dark:text-yellow-300">
              <li>• Ensure your Facebook Lead Forms include privacy policy links</li>
              <li>• Add consent checkbox for data processing (required in EU)</li>
              <li>• Store lead data securely in your CRM</li>
              <li>• Honor data deletion requests within 30 days</li>
              <li>• Don't share Facebook lead data with third parties without consent</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>Next Steps</h2>
      <div className="not-prose space-y-3 mt-6">
        <a
          href="/settings/facebook-pages"
          className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
        >
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Connect Your Facebook Page</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Start capturing leads from Facebook Lead Ads now
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </a>

        <a
          href="/docs/lead-management"
          className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
        >
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Learn Lead Management</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Master how to manage and convert your Facebook leads
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </a>

        <a
          href="/docs/round-robin"
          className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
        >
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Auto-Assign Facebook Leads</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Set up round-robin to instantly distribute leads to your team
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </a>
      </div>

      <div className="not-prose bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 mt-8">
        <div className="flex items-start space-x-3">
          <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-2">
              Ready to Capture Facebook Leads!
            </h3>
            <p className="text-green-800 dark:text-green-300">
              Your CRM is now configured for Facebook Lead Ads integration.
              Connect your Page and start converting social traffic into qualified leads.
              Need help? Contact support at{' '}
              <a href="mailto:support@yourcrm.com" className="underline">
                support@yourcrm.com
              </a>
            </p>
          </div>
        </div>
      </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default FacebookIntegrationDocs;
